import type { AttributeValue, SupplyPointDetail } from "@riea-water/contracts";
import { useRef } from "react";
import { Link, useOutletContext, useParams } from "react-router";
import { useToast } from "../shell/toast.js";
import { RaiseJobSheet, raisedMessage } from "./RaiseJobSheet.js";
import { useReads, useSupplyPoint, useTimeline } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useFindings } from "../api/queries.js";
import { SEVERITY_COLOUR, STATUS_LABEL, statusTone } from "./findings-helpers.js";
import { ConsumptionChart } from "./ConsumptionChart.js";
import {
  attributePill,
  provenanceLine,
  readMethodLabel,
  timelineDate,
  timelineSummary,
  valueText,
} from "./registry-helpers.js";
import { ConfidenceMeter } from "./SupplyPoints.js";

interface Row {
  label: string;
  values: AttributeValue[];
  attribute: string;
  format?: (value: unknown) => string;
}

/** The Record card's rows, in the prototype's order: serial first (both values when in conflict), then location, UPRN, occupancy, read method. */
function recordRows(detail: SupplyPointDetail): Row[] {
  const meter = detail.meters[0];
  const rows: Row[] = [];
  if (meter) {
    rows.push({
      label: "Meter serial",
      values: meter.attributes["serial"] ?? [],
      attribute: "serial",
    });
    rows.push({
      label: "Location",
      values: meter.attributes["location_type"] ?? [],
      attribute: "location_type",
      format: (v) => {
        const type = valueText(v).replace(/_/g, " ");
        const text = type.charAt(0).toUpperCase() + type.slice(1);
        return meter.gridRef
          ? `${text} · ${meter.gridRef}`
          : meter.locationDescription
            ? `${text} · ${meter.locationDescription}`
            : text;
      },
    });
  }
  rows.push({
    label: "UPRN",
    values: detail.premises.uprn
      ? [{ ...(detail.attributes["uprn"] ?? uprnFallback(detail)), value: detail.premises.uprn }]
      : [],
    attribute: "uprn",
  });
  rows.push({
    label: "Occupancy",
    values: detail.attributes["occupancy_status"] ? [detail.attributes["occupancy_status"]] : [],
    attribute: "occupancy_status",
    format: (v) => {
      const since = valueText(detail.attributes["occupancy_since"]?.value);
      const text = valueText(v);
      const label = text.charAt(0).toUpperCase() + text.slice(1);
      return since && since !== "Not held" ? `${label} since ${timelineDate(since)}` : label;
    },
  });
  if (meter) {
    rows.push({
      label: "Read method",
      values: meter.attributes["read_method"] ?? [],
      attribute: "read_method",
      format: (v) => readMethodLabel(String(v) as SupplyPointDetail["readMethod"]),
    });
  }
  return rows;
}

function uprnFallback(detail: SupplyPointDetail): AttributeValue {
  return {
    value: detail.premises.uprn,
    source: "import",
    sourceRef: "address match",
    observedAt: new Date().toISOString(),
    recordedAt: new Date().toISOString(),
    confidence: 0.9,
    evidenceIds: [],
    isCurrent: true,
    inConflict: false,
  };
}

/** The supply point record (REG-02, REG-04, REG-05, LED-04, AC-1-06), as the prototype draws it. */
export function SupplyPoint() {
  const { client, me } = useOutletContext<ShellContext>();
  const { spid = "" } = useParams();
  const raiseDialog = useRef<HTMLDialogElement>(null);
  const { toast, Toast } = useToast();
  const canRaise = ["tenant_admin", "data_lead", "analyst", "field_coordinator"].includes(me.role);
  const detail = useSupplyPoint(client, spid);
  const timeline = useTimeline(client, spid);
  const openFindings = useFindings(client, { spid, limit: 20, sort: "impact_desc" });
  const meterId = detail.data?.meters[0]?.id ?? null;
  const from = new Date(Date.now() - 25 * 30 * 86_400_000).toISOString().slice(0, 10);
  const reads = useReads(client, meterId, from);

  if (detail.isPending) {
    return (
      <>
        <div className="crumb">
          <Link to="/supply-points">Supply points</Link>
          <span className="faint">/</span>
          <span>{spid}</span>
        </div>
        <section className="card" aria-busy="true">
          <div className="body">
            <div className="skeleton" />
            <div className="skeleton" style={{ width: "60%", marginTop: 8 }} />
          </div>
        </section>
      </>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <>
        <div className="crumb">
          <Link to="/supply-points">Supply points</Link>
          <span className="faint">/</span>
          <span>{spid}</span>
        </div>
        <div className="empty">Supply point not found.</div>
      </>
    );
  }
  const d = detail.data;
  const [name, ...rest] = d.address.split(", ");
  const rows = recordRows(d);

  return (
    <>
      <div className="crumb">
        <Link to="/supply-points">Supply points</Link>
        <span className="faint">/</span>
        <span>{d.spid}</span>
      </div>
      <div className="head">
        <div>
          <h1 className="mono" style={{ fontSize: "1.35rem" }}>
            {d.spid}
          </h1>
          <div className="muted">
            {name} · {rest.join(", ")} · {d.retailerName}
          </div>
        </div>
        <div className="split">
          {canRaise ? (
            <button
              className="btn sm"
              type="button"
              onClick={() => raiseDialog.current?.showModal()}
            >
              Raise field job
            </button>
          ) : null}
          <button
            className="btn sm"
            type="button"
            disabled
            title="Bilateral drafts arrive in Phase 5"
          >
            Draft bilateral
          </button>
        </div>
      </div>

      <div className="g2">
        <section className="card">
          <header>
            <h2>Record</h2>
            <ConfidenceMeter score={d.confidenceScore} />
          </header>
          <div className="body">
            {rows.map((row) =>
              row.values.length === 0 ? (
                <div className="kv" key={row.label}>
                  <span className="k">{row.label}</span>
                  <span className="v">
                    Not held
                    <small>No source has given a value</small>
                  </span>
                  <span className="pill warn">Missing</span>
                </div>
              ) : (
                row.values.map((v, i) => {
                  const pill = attributePill(row.values, row.attribute);
                  const own = v.inConflict ? { tone: "crit", label: "Conflict" } : pill;
                  return (
                    <div className="kv" key={`${row.label}-${i}`}>
                      <span className="k">{i === 0 ? row.label : ""}</span>
                      <span className="v">
                        {row.format ? row.format(v.value) : valueText(v.value)}
                        <small>
                          {provenanceLine(v)} · conf {v.confidence.toFixed(2)}
                        </small>
                      </span>
                      <span className={`pill ${own.tone}`}>{own.label}</span>
                    </div>
                  );
                })
              ),
            )}
            <div className="faint" style={{ fontSize: ".74rem", marginTop: 10 }}>
              Confidence {d.confidenceScore} = weighted attribute confidence less open finding
              penalty. Both values are kept when sources disagree.
            </div>
          </div>
        </section>
        <section className="card">
          <header>
            <h2>Open findings</h2>
            <span className="mono faint" style={{ fontSize: ".72rem" }}>
              {d.openFindings}
            </span>
          </header>
          <div className="tablewrap">
            <table>
              <tbody>
                {(openFindings.data?.items ?? [])
                  .filter(
                    (f) =>
                      !["resolved", "verified", "dismissed", "auto_cleared"].includes(f.status),
                  )
                  .map((f) => (
                    <tr key={f.id} className="click">
                      <td className="sev">
                        <i
                          style={{ background: SEVERITY_COLOUR[f.severity] }}
                          aria-label={`Severity ${f.severity}`}
                          role="img"
                        />
                      </td>
                      <td>
                        <Link to={`/findings/${f.id}`}>
                          <b>{f.ruleName}</b>{" "}
                          <span className="mono faint" style={{ fontSize: ".7rem" }}>
                            {f.ruleId}
                          </span>
                        </Link>
                        <br />
                        <span className="muted" style={{ fontSize: ".78rem" }}>
                          {f.explanation}
                        </span>
                      </td>
                      <td>
                        <span className={`pill ${statusTone(f.status)}`}>
                          {STATUS_LABEL[f.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                {openFindings.data && openFindings.data.items.length === 0 ? (
                  <tr>
                    <td className="empty">
                      {d.openFindings === 0
                        ? "No open findings. The next rule run re-checks this record."
                        : `${d.openFindings} open findings`}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="g2">
        <section className="card">
          <header>
            <h2>Consumption · 24 months</h2>
            <span className="faint mono" style={{ fontSize: ".72rem" }}>
              from MDM and reads
            </span>
          </header>
          <div className="body">
            {reads.data ? (
              <ConsumptionChart reads={reads.data} />
            ) : meterId ? (
              <div className="skeleton" />
            ) : (
              <div className="empty">No meter is linked to this supply point.</div>
            )}
          </div>
        </section>
        <section className="card">
          <header>
            <h2>Event ledger</h2>
            <span className="pill ok">chain verified</span>
          </header>
          <div className="body">
            <div className="timeline">
              {(timeline.data ?? []).map((entry, i) => (
                <div className={`tl${entry.derived ? " gap" : ""}`} key={i}>
                  <span className="dt">{timelineDate(entry.occurredAt)}</span>
                  <span className="dot" />
                  <span>
                    {timelineSummary(entry)}
                    <br />
                    <span className="ev">{entry.derived ? "(missing)" : entry.type}</span>
                  </span>
                </div>
              ))}
              {timeline.data && timeline.data.length === 0 ? (
                <div className="empty">No events yet.</div>
              ) : null}
            </div>
          </div>
        </section>
      </div>
      <dialog className="sheet" ref={raiseDialog} aria-labelledby="raise-job-title">
        <RaiseJobSheet
          client={client}
          supplyPointId={d.id}
          onClose={() => raiseDialog.current?.close()}
          onDone={(job) => {
            raiseDialog.current?.close();
            toast(raisedMessage(job));
          }}
        />
      </dialog>
      <Toast />
    </>
  );
}
